
/**
 * Database connection pooling and query optimization utilities
 * Enhanced with memory management and connection timeout handling
 */

export const connectionPoolConfig = {
  // Reduced connection pool settings for memory efficiency
  poolSize: 5, // Reduced from 10
  idleTimeout: 30000, // Reduced to 30 seconds
  acquireTimeout: 15000, // Reduced to 15 seconds
  retryDelayMs: 500, // Reduced retry delay
  maxRetries: 2, // Reduced max retries
};

export const queryOptimization = {
  // Stricter batch size limits to prevent memory overflow
  maxBatchSize: 25, // Reduced from 50
  defaultPageSize: 10, // Reduced from 20
  maxPageSize: 50, // Reduced from 100
  
  // Connection management with timeout handling
  getOptimizedQuery: (baseQuery: any, limit: number = 10) => {
    return baseQuery.limit(Math.min(limit, queryOptimization.maxPageSize));
  },
  
  // Memory-efficient pagination with connection timeout protection
  getPaginatedQuery: (query: any, page: number = 1, pageSize: number = 10) => {
    const safePageSize = Math.min(pageSize, queryOptimization.maxPageSize);
    const offset = (page - 1) * safePageSize;
    return query.range(offset, offset + safePageSize - 1);
  },
};

// Enhanced query result caching with memory management
const queryCache = new Map<string, { data: any; timestamp: number; ttl: number }>();
const MAX_CACHE_SIZE = 500; // Reduced from 1000

export const cacheQuery = (key: string, data: any, ttlMs: number = 180000) => { // Reduced to 3 minutes default
  // Aggressive cache cleanup to prevent memory issues
  if (queryCache.size >= MAX_CACHE_SIZE) {
    const now = Date.now();
    let cleaned = 0;
    
    for (const [cacheKey, entry] of queryCache.entries()) {
      if (now - entry.timestamp > entry.ttl || cleaned < 100) {
        queryCache.delete(cacheKey);
        cleaned++;
      }
    }
    
    // If still too large, clear oldest entries
    if (queryCache.size >= MAX_CACHE_SIZE) {
      const sortedEntries = Array.from(queryCache.entries())
        .sort(([,a], [,b]) => a.timestamp - b.timestamp);
      
      for (let i = 0; i < 100 && queryCache.size >= MAX_CACHE_SIZE; i++) {
        queryCache.delete(sortedEntries[i][0]);
      }
    }
  }
  
  queryCache.set(key, {
    data,
    timestamp: Date.now(),
    ttl: ttlMs,
  });
};

export const getCachedQuery = (key: string) => {
  const entry = queryCache.get(key);
  if (!entry) return null;
  
  if (Date.now() - entry.timestamp > entry.ttl) {
    queryCache.delete(key);
    return null;
  }
  
  return entry.data;
};

// Add cleanup utility function
export const clearStaleCache = () => {
  const now = Date.now();
  let cleared = 0;
  
  for (const [key, entry] of queryCache.entries()) {
    if (now - entry.timestamp > entry.ttl) {
      queryCache.delete(key);
      cleared++;
    }
  }
  
  console.log(`Cleared ${cleared} stale cache entries`);
  return cleared;
};

// Periodic cache cleanup
if (typeof window !== 'undefined') {
  setInterval(clearStaleCache, 60000); // Clean every minute
}
